/**
 * useDismissibleHelp — Persisted dismissal state for help/info banners.
 *
 * Each banner is identified by a stable string key (e.g. `buddy_group_intro`).
 * Dismissals are stored in localStorage scoped to the logged-in user so two
 * people sharing a browser don't see each other's preferences. Logged-out
 * dismissals fall back to an `anon` bucket on the same device.
 *
 * Storage is intentionally a thin layer behind the hook so we can swap in a
 * DB-backed implementation later (e.g. user_dismissed_help_banners) without
 * changing call sites.
 */
import { useCallback, useEffect, useState } from 'react';

import { useAuthStore } from '@/state_management_and_stores/auth.store';

const STORAGE_PREFIX = 'help.dismissed';

function buildStorageKey(bannerKey: string, userId: string | null): string {
  return `${STORAGE_PREFIX}.${userId ?? 'anon'}.${bannerKey}`;
}

function readDismissed(storageKey: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(storageKey) === '1';
  } catch {
    return false;
  }
}

export interface UseDismissibleHelpResult {
  dismissed: boolean;
  dismiss: () => void;
  reopen: () => void;
}

export function useDismissibleHelp(bannerKey: string): UseDismissibleHelpResult {
  const userId = useAuthStore((s) => s.profile?.id ?? null);
  const storageKey = buildStorageKey(bannerKey, userId);

  const [dismissed, setDismissed] = useState<boolean>(() => readDismissed(storageKey));

  // Re-read whenever the storage key changes (login/logout, or banner key prop
  // change). Keeps each user's dismissal scope independent on shared browsers.
  useEffect(() => {
    setDismissed(readDismissed(storageKey));
  }, [storageKey]);

  const dismiss = useCallback(() => {
    setDismissed(true);
    try {
      window.localStorage.setItem(storageKey, '1');
    } catch {
      /* localStorage unavailable — keep in-memory dismissal for the session */
    }
  }, [storageKey]);

  const reopen = useCallback(() => {
    setDismissed(false);
    try {
      window.localStorage.removeItem(storageKey);
    } catch {
      /* localStorage unavailable — in-memory state is enough for re-show */
    }
  }, [storageKey]);

  return { dismissed, dismiss, reopen };
}
